import { UploadIcon } from "@signet/ui";
import { defineWebModule, lazyPage } from "@signet/web-kit";
import { str } from "./api.ts";
import { HealthNowCard } from "./now-cards.tsx";

// Loaded when first opened (web-kit lazy.ts).
const QueuePage = lazyPage(() => import("./queue.tsx"), "QueuePage");
const LedgerPage = lazyPage(() => import("./ledger.tsx"), "LedgerPage");
const SetupPage = lazyPage(() => import("./setup/SetupPage.tsx"), "SetupPage");
const HealthPage = lazyPage(() => import("./health.tsx"), "HealthPage");
const DuplicatePage = lazyPage(() => import("./duplicate.tsx"), "DuplicatePage");
const StatsPage = lazyPage(() => import("./stats.tsx"), "StatsPage");

/* ──────────────────────────────────────────────────────────────────────────
   The web half of Uploader: the engine's queue, ledger, stats and health,
   manual duplication, and the content setup, which replaced the flow editor
   and the flows view on 2026-10-21. Every page reads Signet's engine tables;
   all but the content setup are drawn in the upload system's own look (look/).
   ────────────────────────────────────────────────────────────────────────── */

export default defineWebModule({
  id: "uploader",
  nav: { label: "Flows", icon: UploadIcon, to: "/uploader", needs: "uploader.queue:view" },
  routes: [
    { path: "/uploader", component: QueuePage, needs: "uploader.queue:view", validateSearch: (raw) => ({ status: str(raw.status), creator: str(raw.creator) }) },
    { path: "/uploader/ledger", component: LedgerPage, needs: "uploader.ledger:view", validateSearch: (raw) => ({ q: str(raw.q), creator: str(raw.creator) }) },
    { path: "/uploader/setup", component: SetupPage, needs: "uploader.flow:view", validateSearch: (raw) => ({ creator: str(raw.creator), view: str(raw.view), tpl: str(raw.tpl) }) },
    { path: "/uploader/stats", component: StatsPage, needs: "uploader.ledger:view" },
    { path: "/uploader/duplicate", component: DuplicatePage, needs: "uploader.job:create" },
    { path: "/uploader/health", component: HealthPage, needs: "uploader.health:view" },
  ],
  commands: [
    { id: "uploader.queue", label: "Upload queue", to: "/uploader", needs: "uploader.queue:view", hint: "Flows", keywords: "duplication jobs running stuck retry skip pause eta" },
    { id: "uploader.ledger", label: "Upload ledger", to: "/uploader/ledger", needs: "uploader.ledger:view", hint: "Flows", keywords: "processed media file task done" },
    { id: "uploader.flows", label: "Content setup", to: "/uploader/setup", needs: "uploader.flow:view", hint: "Flows", keywords: "creator flows drive folders steps duplication instagram reddit twitter fansly editor" },
    { id: "uploader.defaults", label: "Default flows", to: "/uploader/setup?view=settings", needs: "uploader.defaults:view", hint: "Flows", keywords: "settings templates flows new creators" },
    { id: "uploader.stats", label: "Upload stats", to: "/uploader/stats", needs: "uploader.ledger:view", hint: "Flows", keywords: "outputs duplicates made totals message categories week" },
    { id: "uploader.duplicate", label: "Duplicate by hand", to: "/uploader/duplicate", needs: "uploader.job:create", hint: "Flows", keywords: "manual duplication enqueue" },
    { id: "uploader.health", label: "Upload system health", to: "/uploader/health", needs: "uploader.health:view", hint: "Flows", keywords: "render slots watcher sweeper drive sign-in lease settings" },
  ],
  // "<name> uploads" is all of her flows (Jay, 2026-10-01).
  creatorPages: [
    { id: "uploader.flows", label: "Uploads", needs: "uploader.flow:view", order: 30, keywords: "flows uploader duplication upload drive", to: (name) => `/uploader/setup?creator=${encodeURIComponent(name)}` },
  ],
  nowCards: {
    "uploader.health": HealthNowCard,
  },
});
